import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import { api } from '../../utils/api';

const COLLECTIONS = [
  { value: 'wiki-chunks', label: 'Wiki Chunks (wiki-chunks)' },
  { value: 'videoEmbeddings', label: 'Video Embeddings (videoEmbeddings)' },
];

export default function VectorDBSearch() {
  const [collection, setCollection] = useState('wiki-chunks');
  const [query, setQuery] = useState('');
  const [topK, setTopK] = useState(8);
  const [folderPath, setFolderPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get('/vector-search/wiki-folders')
      .then((data) => setFolders(data.folders || []))
      .catch(() => setFolders([]));
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const data = await api.post('/vector-search/search', {
        query: query.trim(),
        collection,
        topK,
        ...(collection === 'wiki-chunks' && folderPath ? { folderPath } : {}),
      });
      setResults(data);
    } catch (err) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <StorageIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          VectorDB Search
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Collection selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="collection-label">Collection</InputLabel>
          <Select
            labelId="collection-label"
            value={collection}
            label="Collection"
            onChange={(e) => setCollection(e.target.value)}
          >
            {COLLECTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Folder filter — wiki-chunks only */}
        {collection === 'wiki-chunks' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="folder-label">Folder (optional)</InputLabel>
            <Select
              labelId="folder-label"
              value={folderPath}
              label="Folder (optional)"
              onChange={(e) => setFolderPath(e.target.value)}
            >
              <MenuItem value="">All folders</MenuItem>
              {folders.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Top-K selector */}
        <FormControl sx={{ mb: 2, width: 120 }}>
          <InputLabel id="topk-label">Top K</InputLabel>
          <Select
            labelId="topk-label"
            value={topK}
            label="Top K"
            onChange={(e) => setTopK(e.target.value)}
          >
            {[4, 6, 8, 10, 15, 20].map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Query input */}
        <TextField
          label="Search Query"
          multiline
          rows={4}
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter a natural language query to search the vector database..."
          helperText="Tip: Press Cmd+Enter to search"
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          size="large"
        >
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results && (
        <Box>
          {/* Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">
              Results
            </Typography>
            <Chip
              label={`${results.totalChunks} chunk${results.totalChunks !== 1 ? 's' : ''}`}
              color="primary"
              size="small"
            />
            <Chip
              label={results.collection}
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Sources */}
          {results.sources && results.sources.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Sources ({results.sources.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {results.sources.map((source, i) => (
                  results.collection === 'videoEmbeddings' ? (
                    <Chip
                      key={i}
                      label={source}
                      size="small"
                      component="a"
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      color="secondary"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      key={i}
                      label={source}
                      size="small"
                      variant="outlined"
                    />
                  )
                ))}
              </Box>
            </Paper>
          )}

          {/* RAG Context */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>
                RAG Context (full assembled context)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                {results.ragContext}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Individual chunks */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Chunks
          </Typography>
          {results.chunks.map((chunk, i) => (
            <Paper key={chunk.id || i} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Chip label={`#${i + 1}`} size="small" color="primary" />
                {results.collection === 'wiki-chunks' ? (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      {chunk.filePath}
                    </Typography>
                    <Chip label={`chunk ${chunk.chunkIndex}`} size="small" variant="outlined" />
                    <Chip label={`${chunk.tokenCount} tokens`} size="small" variant="outlined" />
                  </>
                ) : (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      {chunk.title}
                    </Typography>
                    {chunk.url && (
                      <Chip
                        label="Open video"
                        size="small"
                        component="a"
                        href={chunk.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </>
                )}
              </Box>
              <Divider />
              <Box sx={{ p: 2 }}>
                {results.collection === 'wiki-chunks' ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {chunk.text}
                  </ReactMarkdown>
                ) : (
                  <>
                    {chunk.thumbnail && (
                      <Box
                        component="img"
                        src={chunk.thumbnail}
                        alt={chunk.title}
                        sx={{ width: 160, height: 'auto', borderRadius: 1, mb: 1, display: 'block' }}
                      />
                    )}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {chunk.summary}
                    </ReactMarkdown>
                  </>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
